import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, HostListener, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { DynamicFormArray, Props } from '../../model/form.model';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { ExcelExportService } from '../../services/excel-export.service';
import { DataTableSchema, DefaultTableSchema, exportConfig, TableColumnSchema, ToolTipConfig } from '../../model/dynamic-data-table.model';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { CommonUtils } from '../../utils/common.utils';
import { DownloadButtonComponent } from "../../components/download-button/download-button.component";
import { CamelCasePipe } from "../../utils/camel-case.pipe";
import { PaginatorService } from '../../services/paginator-service.service';
import { SearchConfig } from '../../model/api.model';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { HoverOverlayDirective } from '../../../tooltip.directive';
import { MatCardModule } from '@angular/material/card';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatBadgeModule } from '@angular/material/badge';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { HeaderActionButtonComponent } from '../../components/header-action-button/header-action-button.component';
import { CommonService } from '../../services/common.service';

@Component({
  selector: 'app-dynamic-data-table',
  standalone: true,
  imports: [MatCardModule, HoverOverlayDirective, MatTableModule, MatMenuModule,
    MatTooltipModule, MatPaginator, MatSortModule, CommonModule, MatFormFieldModule,
    MatInputModule, MatIconModule, FormsModule, DownloadButtonComponent, CamelCasePipe,
    MatBadgeModule, MatProgressBarModule, HeaderActionButtonComponent],
  providers: [DatePipe, CurrencyPipe, CamelCasePipe],
  templateUrl: './dynamic-data-table.component.html',
  styleUrl: './dynamic-data-table.component.scss'
})
export class DynamicDataTableComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  private readonly searchSubject = new Subject<string>();
  private readonly changeRef = inject(ChangeDetectorRef)
  private subscription!: Subscription;
  private previousDataTableSchema: DataTableSchema | null = null;
  private cachedHasColumnMerge = false;
  private lastDataLength = -1;
  private lastDataHash = '';
  public isFilterDisabled = true;
  @Input() DataTableSchema: DataTableSchema = DefaultTableSchema;
  @Input() apiConfig!: SearchConfig;
  @Input() filterSchema!: DynamicFormArray;
  @Input() set dataInput(value: any[] | null | undefined) {
    const safeValue = Array.isArray(value) ? value : [];
    const inputFilter = this.DataTableSchema?.inputFilter ?? '';

    this.isFilterDisabled = inputFilter.length <= 0 && safeValue.length <= 0;

    if (!this.dataSource) {
      this.initializeDataTable();
    }

    const newDataHash = JSON.stringify([safeValue.length, safeValue[0]?.spinId, safeValue.at(-1)?.spinId]);
    if (this.lastDataHash === newDataHash && this.lastDataLength === safeValue.length) {
      return; 
    }
    this.lastDataHash = newDataHash;
    this.lastDataLength = safeValue.length;
    const mergedData = this.cachedHasColumnMerge ? this.commonService.applyColumnMergesBySpinId(safeValue, this.DataTableSchema?.columnSchema ?? []) : safeValue;    
    this.dataSource.data = mergedData;
    this.DataTableSchema.dataSource = mergedData;
    
    this.isFullHeight = safeValue.length >= 10;

    if (this.DataTableSchema.serverSidePaginatation &&
      this.DataTableSchema.pageSizeOptions?.pageLength !== undefined) {
      this.pageLength = this.DataTableSchema.pageSizeOptions.pageLength;
      if (this.paginator) {
        this.paginator.length = this.pageLength;
        if (this.pageLength === 0) {
          this.paginator.pageIndex = 0;
        }
      }
    }
  };
  @Input() props!: Props;
  @Output() dataEmitter = new EventEmitter<string>();
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) matTable?: MatTable<any>;
  paginatorService = inject(PaginatorService)
  public initializeRender = false;
  public isFullHeight = false;
  public resultsLength = 0;
  public filterApplicableFields: Array<string> | null = null;
  public emptyData = new MatTableDataSource([{ empty: "row" }]);
  public exportColumnInfo: any = {};
  dataSource!: MatTableDataSource<any>;
  flattenedColumns: any[] = [];
  isMultiHeader = false;
  headerRows: string[] = [];
  mainHeaderRows: string[] = [];
  pageSizeOptions: any[] = [];
  pageLength = 0;
  pageSize = 0;
  private stickyRefreshTimer?: ReturnType<typeof setTimeout>;

  constructor(private readonly exportService: ExcelExportService, private readonly commonUtils: CommonUtils, private readonly breakpointObserver: BreakpointObserver,
    private readonly datePipe: DatePipe, private readonly currencyPipe: CurrencyPipe, private readonly camelCasePipe: CamelCasePipe,
  ) { }
  private readonly commonService = inject(CommonService);
  @ViewChild('tableRef', { static: false }) tableRef!: any;
  ngOnInit(): void {
    if (this.DataTableSchema) {
      const data = this.dataSource?.data ? structuredClone(this.dataSource.data) : [];
      this.initializeDataTable()
      this.dynamicSizeOptions();
      this.initializeTableConfig()
      this.dataSource.data = data
    }
    this.initializeRender = true;
  }
  dynamicSizeOptions() {
    if (this.DataTableSchema.pageSizeOptions.pageSize) {
      this.pageSizeOptions = this.DataTableSchema.pageSizeOptions.pageSize;
    }
    this.breakpointObserver.observe([
      Breakpoints.Large,
      Breakpoints.XLarge
    ]).subscribe(result => {
      const pageSize = this.pageSizeOptions[0]; 
      if (result.breakpoints[Breakpoints.XLarge]) {
        // pageSize = this.pageSizeOptions[2];  
      } else {
        return;
      }
      this.pageSize = pageSize;
      if (this.paginator) {
        this.paginator._changePageSize(pageSize);
      }
      this.changeRef.detectChanges();
    });
  }
  initializeTableConfig() {
    this.isMultiHeader = this.DataTableSchema.columnSchema.some(col => col.sub_headers);
    this.mainHeaderRows = this.DataTableSchema.columnSchema.map(c => c.fieldKey)

    if (this.isMultiHeader) {
      this.flattenedColumns = this.DataTableSchema.columnSchema
        .flatMap(col => col?.sub_headers?.map((child: any) => child));
    } else {
      this.flattenedColumns = [...this.DataTableSchema.columnSchema];
    }
    this.DataTableSchema.displayedColumns = this.flattenedColumns.filter(column => !column.hideColumn).map(col => col.fieldKey);

    this.filterApplicableFields = this.DataTableSchema.displayedColumns;
    const filtarableSpecificFields = this.DataTableSchema?.searchInfo?.filtarableSpecificFields;
    if (filtarableSpecificFields && filtarableSpecificFields.length > 0) {
      this.filterApplicableFields = filtarableSpecificFields
    }
  }
  initializeDataTable() {
    try {
      this.dataSource = new MatTableDataSource(this.DataTableSchema.dataSource);
      this.dataSource.filterPredicate = (data: { [x: string]: string; }, filter: string) => {
        const critiriaMatched = this.filterApplicableFields?.find(fieldName => {
          if (data[fieldName]) {
            if (data?.[fieldName].toString().toLowerCase().includes(filter.toString().toLowerCase())) {
              return true;
            }
          }
          return false
        }
        )
        return !!critiriaMatched;
      };
      this.dataSource.sortingDataAccessor = (data: any, sortHeaderId: string): any => {
        const value = data[sortHeaderId];
        if (typeof value === 'string') {
          const date = new Date(value);
          if (!Number.isNaN(date.getTime())) {
            return date.getTime();
          }
          const num = Number(value);
          return Number.isNaN(num) ? value.toLocaleLowerCase() : num;
        }
        return value;
      };

      this.dataSource.sortData = (data: any[], _sort: MatSort) => {
        return data
      };

    } catch (e) {
      console.info('Data Table Error:-', e);
    }
  }
  ngAfterViewInit(): void {
    this.initiateFilter()
    if (!this.DataTableSchema.serverSidePaginatation && this.DataTableSchema?.isPaginationRequired) {
      this.dataSource.paginator = this.paginator;
    }
    if (this.DataTableSchema?.isSortRequired) {
      this.dataSource.sort = this.sort;
    }

    this.sort.sortChange.subscribe(() => {
      if (!this.sort.active || this.sort.direction === '') {
        this.DataTableSchema.activeSorts = this.DataTableSchema.activeSorts.filter(s => s.id !== this.sort.active);
      } else {
        const existingSort = this.DataTableSchema.activeSorts.find(s => s.id === this.sort.active);
        if (existingSort) {
          existingSort.direction = this.sort.direction;
        } else {
          this.DataTableSchema.activeSorts.push({ id: this.sort.active, direction: this.sort.direction });
        }
      }
      if (this.DataTableSchema.serverSidePaginatation) {
        this.DataTableSchema.sortCallback(this.DataTableSchema.activeSorts)
      } else {
        this.dataSource.data = this.commonUtils.clientSideSort(this.dataSource.data, this.DataTableSchema.activeSorts);
      }

    });
    this.scheduleStickyRefresh();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleStickyRefresh(100);
  }

  private scheduleStickyRefresh(delay = 0): void {
    if (this.stickyRefreshTimer) {
      clearTimeout(this.stickyRefreshTimer);
    }
    this.stickyRefreshTimer = setTimeout(() => this.refreshStickyStyles(), delay);
  }

  private refreshStickyStyles(): void {
    requestAnimationFrame(() => {
      const table = this.matTable as any;
      table?.updateStickyColumnStyles?.();
      table?.updateStickyHeaderRowStyles?.();
      table?.updateStickyFooterRowStyles?.();
      table?.renderRows?.();
    });
  }

  ngOnChanges(changes: SimpleChanges) {  
    if (changes["DataTableSchema"]) {
      this.initializeDataTable()
      this.initializeTableConfig()
      if (!this.DataTableSchema.serverSidePaginatation && this.DataTableSchema?.isPaginationRequired) {
        this.dataSource.paginator = this.paginator;
      }
      if (this.DataTableSchema?.isSortRequired && this.sort) {
        this.dataSource.sort = this.sort;
      }
      if (this.previousDataTableSchema !== this.DataTableSchema) {
        this.cachedHasColumnMerge = (this.DataTableSchema?.columnSchema ?? []).some(col => col.isColumnsMerge);
        this.previousDataTableSchema = this.DataTableSchema;
      }
      this.scheduleStickyRefresh();
    }
  }
  applyFilter() {
    const filterValue = this.DataTableSchema.inputFilter?.trim()?.toLowerCase() ?? '';
    if (this.DataTableSchema.serverSidePaginatation) {
      this.DataTableSchema.filterCallback(filterValue)
    } else {
      this.dataSource.filter = filterValue
      const activeRecordCount = this.dataSource?.filteredData?.length ?? 0;
      this.isFullHeight = activeRecordCount >= 10;
    }
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }
  formatCriterias(exportConfig: exportConfig) {
    const selectedFields: Record<string, { label: string, value: any }> = {};
    if (this.filterSchema && this.filterSchema?.FieldsArray?.length > 0) {
      const payload = this.filterSchema['payload']
      if (payload) {
        this.filterSchema.FieldsArray.forEach(filterField => {
          const fieldName = filterField['fieldName']
          if (payload[fieldName] && payload[fieldName] !== '' && payload[fieldName] !== null && payload[fieldName] !== undefined) {
            selectedFields[filterField['fieldName']] = {
              label: filterField['displayName'],
              value: filterField['selectedValue']
            }
            if (filterField['options'] && filterField['options'].length > 0) {
              const selectedValue = filterField['selectedValue'];
              if (Array.isArray(selectedValue)) {
                selectedFields[filterField['fieldName']]['value'] =
                  filterField['options'].filter(x => selectedValue.includes(x['fieldValue'])).map(x => x.fieldLabel).join(', ');
              } else {
                selectedFields[filterField['fieldName']]['value'] = filterField['options'].find(x => x.fieldValue === selectedValue)?.fieldLabel;
              }
            }
          }
        })
      }
    }
    const formattedDataset: any = {};
    Object.values(selectedFields).forEach((field) => {
      if (exportConfig?.isCamelCase.includes(field)) {
        formattedDataset[field['label']] = this.commonUtils.toCamelCase(field['value'])
      } else {
        formattedDataset[field['label']] = field['value']
      }
    })
    return formattedDataset
  }
  async exportTableData() {
    if (this.DataTableSchema?.downloadApiConfig) {
      this.DataTableSchema.isLoading = true;
    }
    const exportedFromApi = await this.commonService.exportUsingDownloadApi(this.DataTableSchema?.downloadApiConfig,this.filterSchema,this.DataTableSchema?.exportConfig);
    if (this.DataTableSchema?.downloadApiConfig) {
      this.DataTableSchema.isLoading = false;
    }
    if (exportedFromApi) {
      return;
    }

    if (this.DataTableSchema.serverSidePaginatation) {
      this.DataTableSchema.exportCallback();
      this.commonUtils.spinner('show');
    } else {
      let data: Array<any> = structuredClone(this.dataSource.data) ?? [];
      if (data.length <= 0) {
        this.commonUtils.showMatSnackBar('Export Failed - No Data Found')
        return;
      }
      const exportConfig = this.DataTableSchema?.exportConfig;
      if (exportConfig) {
        let criteriaDataSet: any = null;
        if (exportConfig?.exportCritiria && this.filterSchema?.payload) {
          criteriaDataSet = {
            tableName: exportConfig['exportMessage'],
            data: this.formatCriterias(exportConfig)
          };
        }
        const sortedData = this.commonUtils.clientSideSort(this.dataSource.data, this.DataTableSchema.activeSorts);
        data = structuredClone(this.commonUtils.formatExcelData(exportConfig, sortedData, this.filterSchema)) ?? [];
        const fileName = exportConfig.fileName ?? '';
        const description = exportConfig.descriptions ?? [];
        this.exportService.exportAsExcelFile(data, fileName, description, criteriaDataSet)
        this.commonUtils.showMatSnackBar('File Exported Successfully');
      }
    }
  }
  pageChanged(event: PageEvent) {
    const { pageIndex, pageSize } = event;
    const length = (!this.DataTableSchema.serverSidePaginatation && this.dataSource?.filteredData)
      ? this.dataSource.filteredData.length
      : event.length;
    const recordsOnCurrentPage = this.getRecordsCountOnPage(pageIndex, pageSize, length);
    this.isFullHeight = recordsOnCurrentPage >= 10;
    if (this.DataTableSchema.serverSidePaginatation) {
      this.paginatorService.updatePageEvent(event);
    }
  }

  getRecordsCountOnPage(pageIndex: number, pageSize: number, length: number): number {
    const count = length - (pageIndex * pageSize);
    return Math.min(pageSize, Math.max(0, count));
  }

  getTotalCount(key: string): any {
    if (!this.DataTableSchema?.footerConfig?.fields?.includes(key)) {
      return '';
    }
    return this.dataSource?.data?.reduce((acc, item) => acc + Number(item[key] || 0), 0);
  }
  filterFieldOnClick(_e: any) {
    if (this.DataTableSchema.inputFilter.length <= 0 && this.dataSource?.data?.length <= 0) {
      this.isFilterDisabled = true;
    } else {
      this.isFilterDisabled = false;
    }
  }
  onSearchInput(event: any) {
    const searchTerm = event.target.value;
    if (searchTerm?.trim()?.length > 0) {
      this.isFullHeight = false;
    }
    this.searchSubject.next(searchTerm);
  }

  onSearchPaste(event: ClipboardEvent) {
    const inputElement = event.target as HTMLInputElement | null;
    if (!inputElement) {
      return;
    }
    setTimeout(() => {
      const pastedValue = inputElement.value ?? '';
      this.DataTableSchema.inputFilter = pastedValue;
      this.onSearchInput({ target: { value: pastedValue } });
    }, 0);
  }

  initiateFilter() {
    this.subscription = this.searchSubject.pipe(
      debounceTime(300)
    ).subscribe(() => {
      this.applyFilter()
    })
  }
  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.stickyRefreshTimer) {
      clearTimeout(this.stickyRefreshTimer);
    }
  }
  isText(value: any): boolean {
    return typeof value === 'string';
  }
   formatCellValue(col: TableColumnSchema, value: any) { 
    if (col.fieldType && col.fieldType === 'Array') {
      if (Array.isArray(value)) {
        if (col?.options) {
          value = value
            .map(val => col.options.find((x: { fieldValue: any; }) => x.fieldValue === val)?.['fieldLabel'] ?? val)
            .filter((v: any) => v !== undefined && v !== null && v !== '')
        }
        value = value.join(',')
      } else {
        value = col.options.find((x: { fieldValue: any; }) => x.fieldValue === value)?.['fieldLabel'] ?? value
      }
      return value
    }
    if (col.isPercent) {
      if (!value || Number.isNaN(value) || Number.isNaN(Number.parseFloat(value))) {
        return '0.00'
      }
      return `${Number.parseFloat(value).toFixed(2)}`;
    }

    if (col.isCurrency) {
      return this.currencyPipe.transform(value) ?? '';
    }
    if(col.isUpperCase){
       return typeof value === 'string' ? value.toUpperCase() : value ?? '';   
     }
    if (typeof value === 'string' && !col.isNotCamelCase) {
      return this.camelCasePipe.transform(value);
    }
    return value;
  }
  formatCellTooltip(data: any, config: ToolTipConfig) {
    if (config['isStatic']) {
      return (config['msg'] ?? 'N/A');
    }
    let value = data[config?.value ?? ''];
    if (config.isDate) {
      value = this.datePipe.transform(value, 'MM/dd/yyyy') ?? '';
    }
     if(config.isCamelPreserveTMNA){
       value =  this.camelCasePipe
      .transform(value)
      .replaceAll(/\btmna\b/gi, 'TMNA')
      .replaceAll(/\btms\b/gi, 'TMS')

    }
    return `${config['name']}: ${value}`


  }

  getTooltipContent(element: any, col: any): string {
    if (!col?.length) { return '' };
    return col.map((toolTip: any) => {
      const value = toolTip.isDate ? new Date(element[toolTip.value]).toLocaleDateString() : element[toolTip.value];
      return `${toolTip.name}: ${value}`;
    }).join('\n');
  }

  isSorted(column: string): boolean {
    return this.DataTableSchema.activeSorts.some(s => s.id === column);
  }
  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  getSortDirection(column: string): string | null {
    const sort = this.DataTableSchema.activeSorts.find(s => s.id === column);
    return sort ? sort.direction : null;
  }

  getSortOrder(column: string): number | null {
    const index = this.DataTableSchema.activeSorts.findIndex(s => s.id === column);
    return index >= 0 ? index + 1 : null;
  }

  shouldHideCell(element: any, columnKey: string): boolean {
    return Array.isArray(element?.__hiddenColumns) && element.__hiddenColumns.includes(columnKey);
  }

  getCellRowSpan(element: any, columnKey: string): number | null {
    const span = element?.__rowSpan?.[columnKey];
    return typeof span === 'number' && span > 1 ? span : null;
  }

  trackByRowId(index: number, row: any): any {
    return row?.spinId || row?.id || index;
  }

}
